// ============================================
// Creator Module - Image Gallery (ES6)
// ============================================

import { showToast, showGlobalLoading, hideGlobalLoading } from '../utils.js';
import { CURRENCY } from '../constants.js';
import { getCurrentUser } from '../app/state.js';
import { ENDPOINTS } from './api.js';
import { authFetch } from '../shared/auth-fetch.js';
import {
  getCurrentEditorData,
  getImageModelList, setImageModelList,
  getGalleryImagePage, setGalleryImagePage,
  getGalleryImageLoading, setGalleryImageLoading,
  getGalleryImageHasMore, setGalleryImageHasMore,
  GALLERY_PAGE_SIZE,
  isCacheValid, setCache, getCache,
  MAX_IMAGES
} from './state.js';
import { addEditorImage, updatePublishButtonState } from './editor.js';
import { showLoadingWithRandomText, hideLoadingWithRandomText } from './autofill/handler.js';
import { getIsSituationalImageMode, setSituationalImage, resetSituationalImageMode } from './editor/situational.js';
import { updateCarbonDisplay } from '../auth/index.js';
import { showConfirm } from '../shared/confirm-modal.js';

// ============================================
// 이미지 변형 상태
// ============================================
let transformSourceImage = null;  // { url } 형태
let isLibrarySelectMode = false;  // 라이브러리 선택 모드
let transformResults = [];  // 현재 소스 이미지로 변형된 결과들
let currentTransformSourceUrl = null;  // 현재 소스 이미지 URL (결과 초기화용)

// ============================================
// 이미지 모달
// ============================================

export async function openImageModal() {
  const modal = document.getElementById('imageSelectionModal');
  if (!modal) return;

  const needsModelLoad = getImageModelList().length === 0 && !isCacheValid('imageModels');
  const needsImageLoad = getCurrentUser() && !isCacheValid('generatedImages');

  if (needsModelLoad || needsImageLoad) {
    showGlobalLoading();
    const loadingEl = document.querySelector('.global-loading-text');
    if (loadingEl) loadingEl.textContent = '이미지 준비 중...';

    try {
      const promises = [];
      if (needsModelLoad) {
        promises.push(loadImageModelsData());
      }
      if (needsImageLoad) {
        promises.push(loadGeneratedImagesFromStorage());
      }
      await Promise.all(promises);
    } catch (error) {
      console.error('이미지 모달 데이터 로드 오류:', error);
    } finally {
      hideGlobalLoading();
    }
  }

  switchImageTab('generate');
  renderImageModelCarousel();
  renderGeneratedImagesGallery();

  modal.classList.add('active');

  if (window.ModalManager) {
    window.ModalManager.push('imageSelectionModal');
  }
}

export function closeImageModal() {
  const modal = document.getElementById('imageSelectionModal');
  if (modal) {
    modal.classList.remove('active');
  }
  window._storyCharImageMode = false;
  window._storyCoverImageMode = false;
  window._storyIntroImageMode = false;
  window._storyIntroImageEditMode = false;

  // 변형 탭 상태 초기화
  isLibrarySelectMode = false;
  resetTransformSourceImage();
  resetTransformResults();

  // 상황별 이미지 모드 초기화
  resetSituationalImageMode();

  if (window.ModalManager) {
    window.ModalManager.remove('imageSelectionModal', true);
  }
}

export async function switchImageTab(tabName) {
  document.querySelectorAll('.image-modal-tab').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.imageTab === tabName);
  });

  document.querySelectorAll('.image-tab-content').forEach(content => {
    content.classList.remove('active');
  });

  const tabContent = document.getElementById(`imageTab${tabName.charAt(0).toUpperCase() + tabName.slice(1)}`);
  if (tabContent) {
    tabContent.classList.add('active');
  }

  // 갤러리가 아닌 탭으로 전환하면 선택 모드 해제
  if (tabName !== 'gallery') {
    isLibrarySelectMode = false;
  }

  if (tabName === 'gallery') {
    initGalleryPagination();

    const grid = document.getElementById('generatedImagesGrid');
    const currentEditorData = getCurrentEditorData();

    // 선택 모드 클래스 토글
    if (grid) {
      grid.classList.toggle('select-mode', isLibrarySelectMode);
    }

    if (!currentEditorData.generatedImages || currentEditorData.generatedImages.length === 0) {
      setGalleryImageLoading(true);
      if (grid) {
        grid.innerHTML = '<div class="gallery-loading"><div class="spinner"></div><span>이미지 불러오는 중...</span></div>';
      }
      await loadGeneratedImagesFromStorage();
      setGalleryImageLoading(false);
    }

    renderGeneratedImagesGallery();

    const galleryTab = document.getElementById('imageTabGallery');
    if (galleryTab) {
      galleryTab.removeEventListener('scroll', handleGalleryScroll);
      galleryTab.addEventListener('scroll', handleGalleryScroll);
    }
  }
}

// ============================================
// 이미지 모델 로드
// ============================================

export async function loadImageModelsData() {
  if (isCacheValid('imageModels')) {
    setImageModelList(getCache().imageModels.data);
    return;
  }

  try {
    const db = firebase.firestore();
    const snapshot = await db.collection('imageModels').get();

    const models = snapshot.docs
      .filter(doc => doc.data().isPublic === true)
      .map(doc => ({
        id: doc.id,
        name: doc.data().name,
        image: doc.data().image,
        order: doc.data().order || 999,
        carbonCost: doc.data().carbonCost || 3
      }))
      .sort((a, b) => a.order - b.order);

    if (models.length > 0) {
      setImageModelList(models);
      setCache('imageModels', models);
    } else {
      setImageModelList([]);
    }
  } catch (error) {
    console.error('이미지 모델 로드 오류:', error);
    setImageModelList([]);
  }
}

// ============================================
// 생성된 이미지 로드
// ============================================

export async function loadGeneratedImagesFromStorage(forceRefresh = false) {
  const user = getCurrentUser();
  if (!user) return;

  const currentEditorData = getCurrentEditorData();
  const cache = getCache();

  if (!forceRefresh && isCacheValid('generatedImages')) {
    currentEditorData.generatedImages = cache.generatedImages.data;
    return;
  }

  try {
    const storage = firebase.storage();

    // generated + transformed 폴더 모두 스캔
    const [generatedResult, transformedResult] = await Promise.all([
      storage.ref(`creations/${user.uid}/generated`).listAll(),
      storage.ref(`creations/${user.uid}/transformed`).listAll()
    ]);

    // 두 결과 병합
    const allItems = [...generatedResult.items, ...transformedResult.items];

    const imageItems = allItems.filter(item =>
      item.name.match(/\.(png|jpg|jpeg|webp|gif)$/i)
    );

    const imagePromises = imageItems.map(async (itemRef) => {
      try {
        const [url, metadata] = await Promise.all([
          itemRef.getDownloadURL(),
          itemRef.getMetadata()
        ]);

        return {
          url: url,
          name: itemRef.name,
          createdAt: metadata.timeCreated,
          size: parseInt(metadata.size)
        };
      } catch (err) {
        console.warn('이미지 메타데이터 조회 실패:', itemRef.name, err.message);
        return null;
      }
    });

    const images = (await Promise.all(imagePromises)).filter(img => img !== null);

    images.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    currentEditorData.generatedImages = images;
    setCache('generatedImages', images);
  } catch (error) {
    console.error('이미지 목록 로드 오류:', error);
    if (cache.generatedImages.data) {
      currentEditorData.generatedImages = cache.generatedImages.data;
    }
  }
}

// ============================================
// 이미지 모델 캐러셀
// ============================================

function renderImageModelCarousel() {
  const carousel = document.getElementById('imageModelCarousel');
  if (!carousel) return;

  const imageModelList = getImageModelList();

  if (imageModelList.length === 0) {
    carousel.innerHTML = '<div style="padding: 20px; color: var(--text-tertiary); font-size: 13px;">사용 가능한 모델이 없습니다</div>';
    return;
  }

  const selectedModel = document.getElementById('imageGenModel')?.value || imageModelList[0]?.id || 'animagine';

  carousel.innerHTML = imageModelList.map(model => `
    <div class="image-model-card ${model.id === selectedModel ? 'selected' : ''}"
         data-action="selectImageModel" data-model="${model.id}">
      <div class="image-model-card-loader">
        <div class="spinner"></div>
      </div>
      <img src="${model.image}" alt="${model.name}" class="image-model-card-image"
           onload="this.previousElementSibling.style.display='none'; this.style.opacity='1';"
           onerror="this.previousElementSibling.style.display='none'; this.style.opacity='1';">
      <div class="image-model-card-overlay">${model.name}</div>
    </div>
  `).join('');

  // 이벤트 위임 설정
  carousel.onclick = (e) => {
    const card = e.target.closest('[data-action="selectImageModel"]');
    if (card) {
      selectImageModel(card.dataset.model);
    }
  };

  const input = document.getElementById('imageGenModel');
  if (input && !input.value && imageModelList.length > 0) {
    input.value = imageModelList[0].id;
  }

  updateImageGenCost();
}

export function selectImageModel(modelId) {
  const input = document.getElementById('imageGenModel');
  if (input) input.value = modelId;

  document.querySelectorAll('#imageModelCarousel .image-model-card').forEach(card => {
    card.classList.toggle('selected', card.dataset.model === modelId);
  });

  updateImageGenCost();
}

export function selectImageCount(count) {
  const input = document.getElementById('imageGenCount');
  if (input) input.value = count;

  document.querySelectorAll('.image-count-btn').forEach(btn => {
    btn.classList.remove('selected');
    if (parseInt(btn.dataset.count) === count) {
      btn.classList.add('selected');
    }
  });

  updateImageGenCost();
}

export function updateImageCost() {
  updateImageGenCost();
}

function updateImageGenCost() {
  const countEl = document.getElementById('imageGenCount');
  const costEl = document.getElementById('imageSelectCost');
  const modelEl = document.getElementById('imageGenModel');

  if (countEl && costEl) {
    const count = parseInt(countEl.value) || 1;
    const selectedModelId = modelEl?.value || '';
    const selectedModel = getImageModelList().find(m => m.id === selectedModelId);
    const costPerImage = selectedModel?.carbonCost || 3;
    costEl.textContent = `${count * costPerImage} ${CURRENCY.UNIT}`;
  }
}

// ============================================
// 이미지 업로드
// ============================================

export async function handleImageUpload(event) {
  const file = event.target.files[0];
  if (!file) return;

  if (getCurrentEditorData().images.length >= MAX_IMAGES) {
    showToast(`이미지는 최대 ${MAX_IMAGES}개까지 추가할 수 있습니다`);
    event.target.value = '';
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    showToast('이미지 크기는 5MB 이하여야 합니다');
    return;
  }

  if (!file.type.startsWith('image/')) {
    showToast('이미지 파일만 업로드할 수 있습니다');
    return;
  }

  try {
    showGlobalLoading('이미지 업로드 중...');

    await uploadImageToStorage(file);

    await loadGeneratedImagesFromStorage(true);
    renderGeneratedImagesGallery();

    switchImageTab('gallery');

    showToast('이미지가 업로드되었습니다');
  } catch (error) {
    console.error('이미지 업로드 실패:', error);
    showToast('이미지 업로드에 실패했습니다');
  } finally {
    hideGlobalLoading();
  }

  event.target.value = '';
}

export async function uploadImageToStorage(file) {
  return new Promise((resolve, reject) => {
    if (!firebase.storage) {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
      return;
    }

    const storage = firebase.storage();
    const userId = getCurrentUser().uid;
    const timestamp = Date.now();
    const path = `creations/${userId}/generated/${timestamp}_${file.name}`;
    const ref = storage.ref(path);

    ref.put(file).then(snapshot => {
      return snapshot.ref.getDownloadURL();
    }).then(url => {
      resolve(url);
    }).catch(reject);
  });
}

// ============================================
// 이미지 생성
// ============================================

export async function generateImages() {
  const user = getCurrentUser();
  if (!user) {
    showToast('로그인이 필요합니다');
    return;
  }

  const analysisMode = isAnalysisMode();

  if (analysisMode) {
    // 캐릭터 분석 생성 모드
    await generateImagesFromAnalysis();
  } else {
    // 일반 생성 모드
    await generateImagesFromDescription();
  }
}

// 캐릭터 분석 기반 이미지 생성
async function generateImagesFromAnalysis() {
  const prompt = document.getElementById('editorCharPrompt')?.value?.trim();
  if (!prompt) {
    showToast('점술가 분석 생성은 점술가 프롬프트를 채워야 가능해요');
    return;
  }

  const modelEl = document.getElementById('imageGenModel');
  const countEl = document.getElementById('imageGenCount');
  const model = modelEl?.value || 'animagine';
  const count = parseInt(countEl?.value) || 1;

  // 에디터 데이터 수집
  const existingData = {
    name: document.getElementById('editorCharName')?.value?.trim() || '',
    gender: document.getElementById('editorCharGender')?.value || '여성',
    age: document.getElementById('editorCharAge')?.value?.trim() || '',
    relationship: document.getElementById('editorCharRelationship')?.value?.trim() || '',
    prompt: prompt,
    worldPrompt: document.getElementById('editorWorldPrompt')?.value?.trim() || ''
  };

  showLoadingWithRandomText('image');

  try {
    const response = await authFetch(ENDPOINTS.autoFillCreation, {
      body: {
        existingData: existingData,
        emptyFields: [],
        needsImage: true,
        needsIntro: false,
        showThoughts: false,
        imageModelId: model,
        imageCount: count
      }
    });

    const result = await response.json();

    if (result.success && result.data?.imageUrl) {
      const currentEditorData = getCurrentEditorData();
      if (!currentEditorData.generatedImages) {
        currentEditorData.generatedImages = [];
      }

      // 생성된 이미지 추가
      const newImage = {
        url: result.data.imageUrl,
        name: `imagegen_${Date.now()}.png`,
        createdAt: new Date().toISOString()
      };
      currentEditorData.generatedImages.unshift(newImage);
      setCache('generatedImages', currentEditorData.generatedImages);
      renderLibraryImages();

      // 이미지 모달 유지하고 라이브러리 바텀시트만 열기
      openLibrarySheet();
      showToast('이미지가 생성되었습니다!');
      updateCarbonDisplay();
    } else {
      const errorMsg = typeof result.error === 'string'
        ? result.error
        : (result.error?.message || '이미지 생성에 실패했습니다');
      showToast(errorMsg);
    }
  } catch (error) {
    console.error('이미지 생성 오류:', error);
    showToast('네트워크 오류가 발생했습니다');
  } finally {
    hideLoadingWithRandomText();
  }
}

// 설명 기반 이미지 생성
async function generateImagesFromDescription() {
  const descriptionEl = document.getElementById('imageGenDescription');
  const modelEl = document.getElementById('imageGenModel');
  const countEl = document.getElementById('imageGenCount');

  const description = descriptionEl?.value?.trim();
  if (!description) {
    showToast('이미지 설명을 입력해주세요');
    return;
  }

  const model = modelEl?.value || 'animagine';
  const count = parseInt(countEl?.value) || 1;

  if (!model) {
    showToast('모델을 선택해주세요');
    return;
  }

  try {
    showLoadingWithRandomText('image');

    const response = await authFetch(ENDPOINTS.generateCreatorImage, {
      body: { description, model, count }
    });

    const result = await response.json();

    if (result.success) {
      const currentEditorData = getCurrentEditorData();
      if (!currentEditorData.generatedImages || currentEditorData.generatedImages.length === 0) {
        await loadGeneratedImagesFromStorage();
      }

      const existingImages = currentEditorData.generatedImages || [];
      currentEditorData.generatedImages = [
        ...result.images,
        ...existingImages
      ];
      setCache('generatedImages', currentEditorData.generatedImages);
      initGalleryPagination();
      renderGeneratedImagesGallery();

      // 이미지 모달 유지하고 라이브러리 바텀시트만 열기
      openLibrarySheet();
      showToast(`${result.images.length}개의 이미지가 생성되었습니다`);

      updateCarbonDisplay();
    } else {
      const errorMsg = typeof result.error === 'string'
        ? result.error
        : (result.error?.message || '이미지 생성에 실패했습니다');
      showToast(errorMsg);
    }
  } catch (error) {
    console.error('이미지 생성 오류:', error);
    showToast('네트워크 오류가 발생했습니다');
  } finally {
    hideLoadingWithRandomText();
  }
}

// ============================================
// 갤러리 페이지네이션
// ============================================

export function initGalleryPagination() {
  setGalleryImagePage(0);
  setGalleryImageLoading(false);
  setGalleryImageHasMore(true);
}

export function renderGeneratedImagesGallery(append = false) {
  const grid = document.getElementById('generatedImagesGrid');
  if (!grid) return;

  const currentEditorData = getCurrentEditorData();
  const allImages = currentEditorData.generatedImages || [];

  if (allImages.length === 0 && getGalleryImageLoading()) {
    grid.innerHTML = '<div class="gallery-loading"><div class="spinner"></div><span>이미지 불러오는 중...</span></div>';
    return;
  }

  if (allImages.length === 0) {
    grid.innerHTML = `
      <div class="gallery-empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.3">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21 15 16 10 5 21"/>
        </svg>
        <span>생성된 이미지가 없습니다</span>
      </div>
    `;
    return;
  }

  const start = getGalleryImagePage() * GALLERY_PAGE_SIZE;
  const end = start + GALLERY_PAGE_SIZE;
  const pageImages = allImages.slice(start, end);

  setGalleryImageHasMore(end < allImages.length);

  const html = pageImages.map((img, i) => {
    const index = start + i;
    return `
      <div class="generated-image-item" data-action="openImageDetail" data-index="${index}">
        <img src="${img.url}" alt="생성된 이미지 ${index + 1}">
      </div>
    `;
  }).join('');

  if (append) {
    grid.insertAdjacentHTML('beforeend', html);
  } else {
    grid.innerHTML = html;
    // 선택 모드 클래스 적용
    grid.classList.toggle('select-mode', isLibrarySelectMode);

    // 이벤트 위임 설정 (한 번만)
    grid.onclick = (e) => {
      const item = e.target.closest('[data-action="openImageDetail"]');
      if (item) {
        const index = parseInt(item.dataset.index, 10);
        // 라이브러리 선택 모드일 때는 변형 소스로 선택
        if (isLibrarySelectMode) {
          handleLibraryImageSelect(index);
        } else {
          openImageDetail(index);
        }
      }
    };
  }

  setGalleryImageLoading(false);
}

function handleGalleryScroll(e) {
  const { scrollTop, scrollHeight, clientHeight } = e.target;

  if (scrollTop + clientHeight >= scrollHeight - 100 && !getGalleryImageLoading() && getGalleryImageHasMore()) {
    setGalleryImageLoading(true);
    setGalleryImagePage(getGalleryImagePage() + 1);
    renderGeneratedImagesGallery(true);
  }
}

// ============================================
// 이미지 상세 보기
// ============================================

export function openImageDetail(index) {
  const currentEditorData = getCurrentEditorData();
  currentEditorData.selectedImageIndex = index;
  const img = currentEditorData.generatedImages[index];

  if (!img) return;

  const modal = document.getElementById('imageDetailModal');
  const imgEl = document.getElementById('imageDetailImg');

  if (modal && imgEl) {
    imgEl.src = img.url;
    modal.style.display = 'flex';

    if (window.ModalManager) {
      window.ModalManager.push('imageDetailModal');
    }
  }
}

export function closeImageDetail() {
  const modal = document.getElementById('imageDetailModal');
  if (modal) {
    modal.style.display = 'none';
  }
  getCurrentEditorData().selectedImageIndex = null;

  if (window.ModalManager) {
    window.ModalManager.remove('imageDetailModal', true);
  }
}

export function applyImage() {
  const currentEditorData = getCurrentEditorData();
  const index = currentEditorData.selectedImageIndex;
  if (index === null || index === undefined) return;

  const img = currentEditorData.generatedImages[index];
  if (!img) return;

  // 스토리 모드 분기 처리
  if (window._storyCharImageMode) {
    if (typeof window.StoryCreatorModule !== 'undefined' && window.StoryCreatorModule.applyCharacterImage) {
      window.StoryCreatorModule.applyCharacterImage(img.url);
    }
    window._storyCharImageMode = false;
    closeImageDetail();
    closeImageModal();
    showToast('이미지가 적용되었습니다');
    return;
  }

  if (window._storyCoverImageMode) {
    if (typeof window.StoryCreatorModule !== 'undefined' && window.StoryCreatorModule.applyCoverImage) {
      window.StoryCreatorModule.applyCoverImage(img.url);
    }
    window._storyCoverImageMode = false;
    closeImageDetail();
    closeImageModal();
    showToast('이미지가 적용되었습니다');
    return;
  }

  if (window._storyIntroImageMode) {
    if (window._storyIntroImageEditMode) {
      if (typeof window.StoryCreatorModule !== 'undefined' && window.StoryCreatorModule.replaceIntroImage) {
        window.StoryCreatorModule.replaceIntroImage(img.url);
      }
      window._storyIntroImageEditMode = false;
    } else {
      if (typeof window.StoryCreatorModule !== 'undefined' && window.StoryCreatorModule.applyIntroImage) {
        window.StoryCreatorModule.applyIntroImage(img.url);
      }
    }
    window._storyIntroImageMode = false;
    closeImageDetail();
    closeImageModal();
    showToast('이미지가 적용되었습니다');
    return;
  }

  // 상황별 이미지 모드 처리
  if (getIsSituationalImageMode()) {
    setSituationalImage(img.url);
    resetSituationalImageMode();
    closeImageDetail();
    closeImageModal();
    showToast('이미지가 선택되었습니다');
    return;
  }

  // 기본 캐릭터 에디터 로직
  if (getCurrentEditorData().images.length >= MAX_IMAGES) {
    showToast(`이미지는 최대 ${MAX_IMAGES}개까지 추가할 수 있습니다`);
    return;
  }

  addEditorImage(img.url, null);
  closeImageDetail();
  closeImageModal();
  showToast('이미지가 적용되었습니다');
}

export function downloadImage() {
  const currentEditorData = getCurrentEditorData();
  const index = currentEditorData.selectedImageIndex;
  if (index === null || index === undefined) return;

  const img = currentEditorData.generatedImages[index];
  if (img) {
    const a = document.createElement('a');
    a.href = img.url;
    a.download = `generated_image_${Date.now()}.png`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

export async function deleteGeneratedImage() {
  const currentEditorData = getCurrentEditorData();
  const index = currentEditorData.selectedImageIndex;
  if (index === null || index === undefined) return;

  const confirmed = await showConfirm({
    message: '이 이미지를 삭제하시겠습니까?',
    confirmText: '삭제',
    isDanger: true
  });
  if (confirmed) {
    currentEditorData.generatedImages.splice(index, 1);
    closeImageDetail();
    renderGeneratedImagesGallery();
    showToast('이미지가 삭제되었습니다');
  }
}

// ============================================
// 이미지 변형 기능
// ============================================

// 바텀시트 열기
export function openSourceImageSheet() {
  const sheet = document.getElementById('sourceImageSheet');
  if (sheet) {
    sheet.classList.add('open');
    if (window.ModalManager) {
      window.ModalManager.push('sourceImageSheet');
    }
  }
}

// 바텀시트 닫기
export function closeSourceImageSheet() {
  const sheet = document.getElementById('sourceImageSheet');
  if (sheet) {
    sheet.classList.remove('open');
    if (window.ModalManager) {
      window.ModalManager.remove('sourceImageSheet', true);
    }
  }
}

// 기기에서 업로드 (변형용)
export async function uploadSourceImage(event) {
  const file = event.target.files[0];
  if (!file) return;

  // 파일 검증
  if (file.size > 5 * 1024 * 1024) {
    showToast('이미지 크기는 5MB 이하여야 합니다');
    event.target.value = '';
    return;
  }

  if (!file.type.startsWith('image/')) {
    showToast('이미지 파일만 업로드할 수 있습니다');
    event.target.value = '';
    return;
  }

  showGlobalLoading('이미지 업로드 중...');
  try {
    // Storage에 업로드
    const url = await uploadImageToStorage(file);

    // 라이브러리 갱신
    await loadGeneratedImagesFromStorage(true);

    // 소스 이미지로 설정
    setTransformSourceImage(url);
    closeSourceImageSheet();
    showToast('이미지가 선택되었습니다');
  } catch (error) {
    console.error('[uploadSourceImage] 오류:', error);
    showToast('이미지 업로드에 실패했습니다');
  } finally {
    hideGlobalLoading();
    event.target.value = '';
  }
}

// 라이브러리에서 선택 모드 진입
export function enterLibrarySelectMode() {
  isLibrarySelectMode = true;
  closeSourceImageSheet();
  switchImageTab('gallery');
  showToast('이미지를 선택해주세요');
}

// 라이브러리 이미지 선택 핸들러
function handleLibraryImageSelect(index) {
  const currentEditorData = getCurrentEditorData();
  const img = currentEditorData.generatedImages[index];

  if (img) {
    setTransformSourceImage(img.url);
    isLibrarySelectMode = false;
    switchImageTab('transform');
    showToast('이미지가 선택되었습니다');
  }
}

// 소스 이미지 설정
export function setTransformSourceImage(url) {
  // 소스 이미지가 변경되면 결과 초기화
  if (currentTransformSourceUrl !== url) {
    currentTransformSourceUrl = url;
    resetTransformResults();
  }

  transformSourceImage = { url };

  // UI 업데이트
  const placeholder = document.querySelector('.transform-source-placeholder');
  const preview = document.querySelector('.transform-source-preview');

  if (placeholder && preview) {
    placeholder.style.display = 'none';
    preview.src = url;
    preview.style.display = 'block';
  }
}

// 소스 이미지 초기화
function resetTransformSourceImage() {
  transformSourceImage = null;

  const placeholder = document.querySelector('.transform-source-placeholder');
  const preview = document.querySelector('.transform-source-preview');

  if (placeholder && preview) {
    placeholder.style.display = 'flex';
    preview.src = '';
    preview.style.display = 'none';
  }

  // 입력 필드 초기화
  const promptEl = document.getElementById('transformPrompt');
  if (promptEl) promptEl.value = '';

  const countEl = document.getElementById('transformCount');
  if (countEl) countEl.value = '1';

  updateTransformCost();
}

// 카본 비용 업데이트
export function updateTransformCost() {
  const count = parseInt(document.getElementById('transformCount')?.value) || 1;
  const costEl = document.getElementById('transformCost');
  if (costEl) {
    costEl.textContent = count * 20;
  }
}

// 이미지 변형 API 호출
export async function transformImage() {
  const user = getCurrentUser();
  if (!user) {
    showToast('로그인이 필요합니다');
    return;
  }

  if (!transformSourceImage?.url) {
    showToast('변형할 이미지를 선택해주세요');
    return;
  }

  const prompt = document.getElementById('transformPrompt')?.value?.trim();
  if (!prompt) {
    showToast('변형 설명을 입력해주세요');
    return;
  }

  const count = parseInt(document.getElementById('transformCount')?.value) || 1;

  showLoadingWithRandomText('이미지 변형 중...');
  try {
    const response = await authFetch(ENDPOINTS.transformImage, {
      body: {
        sourceImageUrl: transformSourceImage.url,
        prompt: prompt,
        count: count
      }
    });

    const result = await response.json();

    if (result.success) {
      // 생성된 이미지들을 갤러리에 추가
      const currentEditorData = getCurrentEditorData();
      currentEditorData.generatedImages = [
        ...result.images,
        ...(currentEditorData.generatedImages || [])
      ];
      setCache('generatedImages', currentEditorData.generatedImages);

      // 변형 결과를 현재 탭에 표시
      transformResults = [...result.images, ...transformResults];
      renderTransformResults();

      showToast(`${result.images.length}개의 이미지가 변형되었습니다`);
      updateCarbonDisplay();
    } else {
      const errorMsg = typeof result.error === 'string'
        ? result.error
        : (result.error?.message || '이미지 변형에 실패했습니다');
      showToast(errorMsg);
    }
  } catch (error) {
    console.error('[transformImage] 오류:', error);
    showToast('네트워크 오류가 발생했습니다');
  } finally {
    hideLoadingWithRandomText();
  }
}

// 변형 결과 렌더링
function renderTransformResults() {
  const section = document.getElementById('transformResultsSection');
  const grid = document.getElementById('transformResultsGrid');
  const countEl = document.getElementById('transformResultsCount');

  if (!section || !grid) return;

  if (transformResults.length === 0) {
    section.style.display = 'none';
    return;
  }

  section.style.display = 'block';
  if (countEl) countEl.textContent = `${transformResults.length}장`;

  grid.innerHTML = transformResults.map((img, index) => `
    <div class="transform-result-item" data-action="openTransformResult" data-index="${index}">
      <img src="${img.url}" alt="변형 이미지" loading="lazy">
    </div>
  `).join('');
}

// 변형 결과 이미지 클릭 핸들러
export function openTransformResultDetail(index) {
  const img = transformResults[index];
  if (!img) return;

  // 라이브러리 상세 모달 재활용
  const modal = document.getElementById('libraryImageDetailModal');
  const imgEl = document.getElementById('libraryDetailImage');
  const ddayEl = document.getElementById('libraryDetailDday');

  if (imgEl) imgEl.src = img.url;
  if (ddayEl) {
    const days = calculateDaysRemaining(img.createdAt);
    ddayEl.textContent = `D-${days}`;
    ddayEl.classList.toggle('urgent', days <= 7);
  }

  // 현재 선택된 변형 결과 인덱스 저장
  currentLibraryImageIndex = null;  // 라이브러리 인덱스는 null
  window._currentTransformResultIndex = index;  // 변형 결과 인덱스

  if (modal) {
    modal.style.display = 'flex';
    if (window.ModalManager) {
      window.ModalManager.push('libraryImageDetailModal');
    }
  }
}

// 소스 이미지 변경 시 결과 초기화
function resetTransformResults() {
  transformResults = [];
  renderTransformResults();
}

// ============================================
// 에디터 이미지 액션 바텀시트
// ============================================

export function openEditorImageSheet() {
  const sheet = document.getElementById('editorImageActionSheet');
  if (sheet) {
    sheet.classList.add('open');
    document.body.style.overflow = 'hidden';
    if (window.ModalManager) {
      window.ModalManager.push('editorImageActionSheet');
    }
  }
}

export function closeEditorImageSheet() {
  const sheet = document.getElementById('editorImageActionSheet');
  if (sheet) {
    sheet.classList.remove('open');
    document.body.style.overflow = '';
    if (window.ModalManager) {
      window.ModalManager.remove('editorImageActionSheet', true);
    }
  }
}

// 기기에서 직접 업로드 (라이브러리 거치지 않고 바로 적용)
export async function uploadFromDevice(event) {
  const file = event.target.files?.[0];
  if (!file) return;

  if (!file.type.startsWith('image/')) {
    showToast('이미지 파일만 업로드 가능합니다');
    event.target.value = '';
    return;
  }

  if (file.size > 10 * 1024 * 1024) {
    showToast('이미지 크기는 10MB 이하여야 합니다');
    event.target.value = '';
    return;
  }

  closeEditorImageSheet();
  showGlobalLoading('이미지 업로드 중...');

  try {
    const url = await uploadImageToStorage(file);

    // 상황별 이미지 모드 처리
    if (getIsSituationalImageMode()) {
      setSituationalImage(url, file);
      resetSituationalImageMode();
      showToast('이미지가 선택되었습니다');
    } else {
      addEditorImage(url, null);
      showToast('이미지가 추가되었습니다');
    }
  } catch (error) {
    console.error('[uploadFromDevice] 오류:', error);
    showToast('이미지 업로드에 실패했습니다');
  } finally {
    hideGlobalLoading();
    event.target.value = '';
  }
}

// ============================================
// 라이브러리 바텀시트
// ============================================

let isTransformSourceSelectMode = false;
let currentLibraryImageIndex = null;

export function setTransformSourceSelectMode(enabled) {
  isTransformSourceSelectMode = enabled;
}

export async function openLibrarySheet() {
  closeEditorImageSheet();
  closeSourceImageSheet();

  const sheet = document.getElementById('libraryBottomSheet');
  if (!sheet) return;

  sheet.classList.add('open');
  document.body.style.overflow = 'hidden';
  if (window.ModalManager) {
    window.ModalManager.push('libraryBottomSheet');
  }

  const container = document.getElementById('libraryImagesContainer');
  if (container) {
    container.innerHTML = '<div class="library-loading"><div class="spinner"></div></div>';
  }

  const user = getCurrentUser();
  if (user) {
    await loadGeneratedImagesFromStorage(true);
  }
  renderLibraryImages();
}

export function closeLibrarySheet() {
  const sheet = document.getElementById('libraryBottomSheet');
  if (sheet) {
    sheet.classList.remove('open');
    document.body.style.overflow = '';
    if (window.ModalManager) {
      window.ModalManager.remove('libraryBottomSheet', true);
    }
  }
  isTransformSourceSelectMode = false;
}

// D-day 계산
function calculateDaysRemaining(createdAt) {
  const created = new Date(createdAt);
  const now = new Date();
  const diffTime = created.getTime() + (30 * 24 * 60 * 60 * 1000) - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

// 라이브러리 이미지 렌더링 (D-day 포함)
export function renderLibraryImages() {
  const container = document.getElementById('libraryImagesContainer');
  if (!container) return;

  const currentEditorData = getCurrentEditorData();
  const images = currentEditorData.generatedImages || [];

  if (images.length === 0) {
    container.innerHTML = `
      <div class="library-empty">
        <p>생성된 이미지가 없습니다</p>
      </div>
    `;
    return;
  }

  container.innerHTML = images.map((img, index) => {
    const daysRemaining = calculateDaysRemaining(img.createdAt);
    const isUrgent = daysRemaining <= 7;

    return `
      <div class="library-image-item" data-action="selectLibraryImage" data-index="${index}">
        <img src="${img.url}" alt="생성된 이미지" loading="lazy">
        <span class="library-dday-badge ${isUrgent ? 'urgent' : ''}">D-${daysRemaining}</span>
      </div>
    `;
  }).join('');
}

// 라이브러리 이미지 클릭 핸들러
export function handleLibraryImageClick(index) {
  const currentEditorData = getCurrentEditorData();
  const images = currentEditorData.generatedImages || [];
  const img = images[index];
  if (!img) return;

  if (isTransformSourceSelectMode) {
    setTransformSourceImage(img.url);
    isTransformSourceSelectMode = false;
    closeLibrarySheet();
    showToast('이미지가 선택되었습니다');
  } else {
    openLibraryImageDetail(index);
  }
}

// ============================================
// 라이브러리 이미지 상세 모달
// ============================================

export function openLibraryImageDetail(index) {
  const currentEditorData = getCurrentEditorData();
  const images = currentEditorData.generatedImages || [];
  const img = images[index];
  if (!img) return;

  currentLibraryImageIndex = index;

  const modal = document.getElementById('libraryImageDetailModal');
  const imgEl = document.getElementById('libraryDetailImage');
  const ddayEl = document.getElementById('libraryDetailDday');

  if (imgEl) imgEl.src = img.url;
  if (ddayEl) {
    const days = calculateDaysRemaining(img.createdAt);
    ddayEl.textContent = `D-${days}`;
    ddayEl.classList.toggle('urgent', days <= 7);
  }

  if (modal) {
    modal.style.display = 'flex';
    if (window.ModalManager) {
      window.ModalManager.push('libraryImageDetailModal');
    }
  }
}

export function closeLibraryDetail() {
  const modal = document.getElementById('libraryImageDetailModal');
  if (modal) {
    modal.style.display = 'none';
  }
  currentLibraryImageIndex = null;

  if (window.ModalManager) {
    window.ModalManager.remove('libraryImageDetailModal', true);
  }
}

export function downloadLibraryImage() {
  let img = null;

  // 변형 결과에서 다운로드하는 경우
  if (window._currentTransformResultIndex !== undefined && window._currentTransformResultIndex !== null) {
    img = transformResults[window._currentTransformResultIndex];
  } else {
    // 라이브러리에서 다운로드하는 경우
    const currentEditorData = getCurrentEditorData();
    const images = currentEditorData.generatedImages || [];
    img = images[currentLibraryImageIndex];
  }

  if (!img) return;

  const link = document.createElement('a');
  link.href = img.url;
  link.download = `image_${Date.now()}.png`;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  showToast('다운로드를 시작합니다');
}

export function applyLibraryImage() {
  let img = null;
  let isFromTransformResult = false;

  // 변형 결과에서 적용하는 경우
  if (window._currentTransformResultIndex !== undefined && window._currentTransformResultIndex !== null) {
    img = transformResults[window._currentTransformResultIndex];
    window._currentTransformResultIndex = null;
    isFromTransformResult = true;
  } else {
    // 라이브러리에서 적용하는 경우
    const currentEditorData = getCurrentEditorData();
    const images = currentEditorData.generatedImages || [];
    img = images[currentLibraryImageIndex];
  }

  if (!img) return;

  // 상황별 이미지 모드 처리
  if (getIsSituationalImageMode()) {
    setSituationalImage(img.url);
    resetSituationalImageMode();
    closeLibraryDetail();
    closeLibrarySheet();
    showToast('이미지가 선택되었습니다');
    return;
  }

  if (getCurrentEditorData().images.length >= MAX_IMAGES) {
    showToast(`이미지는 최대 ${MAX_IMAGES}개까지 추가할 수 있습니다`);
    return;
  }

  addEditorImage(img.url, null);
  closeLibraryDetail();
  closeLibrarySheet();
  closeImageModal();

  showToast('이미지가 추가되었습니다');
}

// 소스 이미지 선택을 위해 라이브러리 열기 (변형 탭에서 호출)
export function openLibraryForTransformSource() {
  closeSourceImageSheet();
  setTransformSourceSelectMode(true);
  openLibrarySheet();
  showToast('소스로 사용할 이미지를 선택하세요');
}

// ============================================
// 이미지 생성 모드 선택
// ============================================

// 이미지 생성 모드 변경 핸들러
export function handleGenModeChange() {
  const descriptionMode = document.querySelector('input[name="imageGenMode"][value="description"]');
  const descField = document.getElementById('imageDescriptionField');
  const countSelect = document.getElementById('imageGenCount');

  if (descriptionMode?.checked) {
    // 직접 설명 입력 모드: 설명 필드와 장수 선택 표시
    if (descField) descField.style.display = 'block';
    if (countSelect) countSelect.style.display = '';
  } else {
    // 캐릭터 분석 생성 모드: 설명 필드와 장수 선택 숨김 (1장만 생성)
    if (descField) descField.style.display = 'none';
    if (countSelect) {
      countSelect.style.display = 'none';
      countSelect.value = '1';  // 1장으로 리셋
    }
    document.getElementById('imageGenDescription').value = '';
  }
  updateImageGenCost();
}

// 현재 이미지 생성 모드 확인
export function isAnalysisMode() {
  const analysisMode = document.querySelector('input[name="imageGenMode"][value="analysis"]');
  return analysisMode?.checked ?? true;
}
